import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import Icon from '@cx-web/components/primitives/icon';
import Title, { Header } from '@cx-web/components/primitives/title';
import { captureRemixErrorBoundaryError } from '@sentry/remix';

import { type Country, type Repub, REPUBS } from '@cx-modules/constants';

import { type BetslipProviderData, type CallbackFn, useBetslipContext } from '~/providers/betslip-context';
import { useRepubContext } from '~/providers/repub-context';
import { type ServicesProviderData, useServicesContext } from '~/providers/services-context';
import { TRACKING_ACTIONS, TRACKING_CATEGORIES, TRACKING_EVENTS } from '~/services/actions/tracking-actions';

import ButtonSection from './components/button-section';
import MultiBetslip from './components/multi-betslip';
import EmptyBetslip from './icons/empty-betslip';
import LoadingBetslip from './icons/loading-betslip';
import styles from './style/betslip.module.css';

interface WithContext {
  betslipContext: BetslipProviderData;
  repub: Repub;
  servicesContext: ServicesProviderData;
}

interface IBetslipProps extends WithContext {
  className?: string;
  country?: Country;
}
class Betslip extends React.Component<IBetslipProps> {
  constructor(public props: IBetslipProps) {
    super(props);

    this.setupCallbacksListener();
  }

  private setupCallbacksListener = () => {
    if (typeof window === 'undefined') return;
    const { betslipContext } = this.props;
    const allCallbacks = betslipContext.callbacksRef.current;

    window.ocBetslip = window.ocBetslip || {};
    window.ocBetslip.setCallbacks = (callbacks: Record<string, CallbackFn>) => {
      Object.entries(callbacks).forEach(([key, callback]) => {
        allCallbacks[key] = [...(allCallbacks[key] ?? []), callback];
        if (key === 'syncBetslip') betslipContext.syncModulesBetslipData();
      });
    };
  };

  componentDidMount() {
    const { betslipContext } = this.props;
    const { addOrRemoveFromBetslip } = betslipContext;

    window.bettingActions = {
      ...(window.bettingActions || {}),
      addOrRemoveFromBetslip
    };
  }

  private handleBetslipToggle = (): void => {
    const { betslipContext, servicesContext } = this.props;
    const { open: betslipOpen, toggleBetslip } = betslipContext;
    const event = !betslipOpen ? TRACKING_EVENTS.BET_SLIP_OPEN : TRACKING_EVENTS.BET_SLIP_CLOSE;

    servicesContext.trackingActions.eventTracking({
      event,
      eventCategory: TRACKING_CATEGORIES.BETTING,
      eventAction: TRACKING_ACTIONS[event],
      eventLabel: ''
    });

    toggleBetslip();
  };

  private renderEmptyBetslip = (): JSX.Element => {
    const { betslipContext, repub, servicesContext } = this.props;
    const { open: betslipOpen } = betslipContext;
    const isGZRepub = repub === REPUBS.GZ.name;

    return (
      <div className={[styles.emptyBetslip, betslipOpen ? styles.open : ''].join(' ')}>
        {!isGZRepub &&
        <div className={styles.iconWrapper}>
            <EmptyBetslip />
          </div>
        }

        <Title
          className={styles.title}
          tag={Header.SPAN}
          title={servicesContext.labelActions.get('NOTHING_TO_SEE_HERE')} />


        <p className={styles.info}>{servicesContext.labelActions.get('BETSLIP_IS_EMPTY')}</p>
      </div>);

  };

  private renderBetslipContent = (): JSX.Element => {
    const { betslipContext, servicesContext } = this.props;
    const { count, loading, open: betslipOpen, selectedBookie } = betslipContext;

    if (loading) {
      return (
        <div className={[styles.emptyBetslip, betslipOpen ? styles.open : ''].join(' ')}>
          <div className={styles.loadingIconWrapper}>
            <LoadingBetslip />
          </div>

          <Title className={styles.title} tag={Header.SPAN} title={servicesContext.labelActions.get('ALMOST_THERE')} />

          <p className={styles.info}>{servicesContext.labelActions.get('LOADING_BETS')}</p>
        </div>);

    }

    if (count >= 1 && selectedBookie) {
      return <MultiBetslip />;
    }

    return this.renderEmptyBetslip();
  };

  private renderTitle = () => {
    const { betslipContext, servicesContext } = this.props;
    const { count } = betslipContext;

    switch (true) {
      case count === 1:
        return servicesContext.labelActions.get('BETSLIP_SINGLE');
      case count > 1:
        return servicesContext.labelActions.get('BETSLIP_MULTI');
      default:
        return servicesContext.labelActions.get('BETSLIP');
    }
  };

  render(): JSX.Element {
    const { betslipContext, className } = this.props;
    const { count, open: betslipOpen } = betslipContext;

    return (
      <ErrorBoundary
        fallbackRender={({ error }) => {
          captureRemixErrorBoundaryError(error);
          return null;
        }}>

        <div
          className={[styles.betslipContainer, betslipOpen ? styles.open : '', className || ''].join(' ')}
          data-testid="betslip">

          <div className={styles.header}>
            <div className={styles.content}>
              <span className={styles.counter}>{count}</span>
              <span className={styles.title}>{this.renderTitle()}</span>
            </div>
            <span className={styles.closeIcon} data-testid="close-betslip" onClick={this.handleBetslipToggle}>
              <Icon height={32} icon="Close" width={32} />
            </span>
          </div>
          {this.renderBetslipContent()}
          <ButtonSection />
        </div>
      </ErrorBoundary>);

  }
}

export function withContext<T extends WithContext = WithContext>(Component: React.ComponentType<T>) {
  const WrappedComponent = (props: Omit<T, keyof WithContext>) => {
    const { repub } = useRepubContext();
    const betslipContext = useBetslipContext();
    const servicesContext = useServicesContext();

    return (
      <Component {...props as T} betslipContext={betslipContext} repub={repub} servicesContext={servicesContext} />);

  };

  const displayName = Component.displayName || Component.name || 'Betslip';
  WrappedComponent.displayName = `withContext(${displayName})`;

  return WrappedComponent;
}

const BetslipWithContextAndStoreSlices = withContext(Betslip);

export default BetslipWithContextAndStoreSlices;